import {
  useState,
  PropsWithChildren,
  useMemo,
  useCallback,
  useEffect,
} from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { TimePicker } from "@material-ui/pickers";
import { Add, Edit, EditOff } from "@mui/icons-material";
import { DateTime } from "luxon";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import {
  Service,
  ServiceBuilding,
  BucketImage,
  BucketAttachment,
  ServiceType,
  serviceTypeOpts,
  ServiceProvidedBy,
  ServiceTimeSlot,
  ServiceOverridableProps,
  ServiceDaySlotAvailabilityOptionsModes,
  serviceDaySlotAvailabilityOptions,
  ServiceLaundryOptions,
  ComposableServiceConfig,
  ServiceFormQuestion,
  ServiceFormQuestionUIModel,
  ServiceFormQuestionGqlInputModel,
  ComposableServiceConfigPaymentMode,
} from "../../../../graphql/models";

import useServiceFormStyle from "./service_form.style";

import {
  AttachmentsPicker,
  DialogWithActions,
  GqlRecordPicker,
  I18NTextField,
  ImagePicker,
  PhoneInput,
  ServiceBuildingsPicker,
  ServiceCompositionOptsEditor,
  ServiceLaundryOptionsEditor,
  ServiceListItemsEditorTabs,
  ServiceTabPanel,
} from "../../../components";
import { useLoggedUserInfo, useSnackbarMutation } from "../../../../hooks";
import { i18nForFields } from "../../../../i18n";
import { ServiceSurvey } from "../../../components/service_survey";
import {
  BookingInfo,
  BookingInfoServiceTypeEnum,
} from "../../../components/booking/booking_info";
import { ServiceCategory } from "../types/serviceCategory.type";
import { ServiceMacroCatForm } from "./ServiceMacroCatForm";
import { BookingInfoDto } from "../../../../graphql/models/Booking";

export interface ServiceEditFormProps extends PropsWithChildren<any> {
  value: Service | undefined;
  serviceCategories?: ServiceCategory[];
  onValueUpdatedSuccessfully: (v: Service) => void;
  onServiceCategoryCreate: (v: ServiceCategory) => void;
  onServiceCategoryUpdate: (v: ServiceCategory) => void;
}

export function ServiceForm({
  value,
  serviceCategories,
  onValueUpdatedSuccessfully,
  onServiceCategoryCreate,
  onServiceCategoryUpdate,
}: ServiceEditFormProps) {
  const classes = useServiceFormStyle();

  const { isServiceProviderUser, isAdminUser } = useLoggedUserInfo();

  let mutation = MUTATIONS.CREATE_SERVICE;
  if (value && (value as Service).id) {
    mutation = MUTATIONS.UPDATE_SERVICE;
  }

  const [
    invokeCreateOrUpdateService,
    { loading, data: createOrUpdateServiceData },
  ] = useSnackbarMutation(mutation);
  const [updated, setUpdated] = useState(false);

  const [buildings, setBuildings] = useState<ServiceBuilding[]>(
    value?.buildings ?? []
  );
  const [i18nFields, setI18nFields] = useState<
    Partial<
      i18nForFields<
        | "name"
        | "description"
        | "infoBoxTitle"
        | "infoBoxContent"
        | "reservationDeleteConditions"
        | "reservationUsefulInfoBoxContent"
      >
    >
  >(value?.i18n ?? {});
  // const [name, setName] = useState<string>(value?.name ?? "");
  // const [description, setDescription] = useState<string>(
  //   value?.description ?? ""
  // );
  const [phone, setPhone] = useState<string | undefined>(value?.phone);
  const [image, setImage] = useState<BucketImage | undefined>(value?.image);
  const [attachments, setAttachments] = useState<BucketAttachment[]>(
    value?.attachments ?? []
  );
  const [type, setType] = useState<number | undefined>(value?.type);
  const [macroCats, setMacroCats] = useState<string[]>([]);

  const sortedCategories = useMemo(() => {
    const map = serviceCategories!.map((item) => ({
      ...item,
      sortIndex: item.sortIndex ?? 999,
    }));
    map.sort((a, b) => a.sortIndex - b.sortIndex);
    return map;
  }, [serviceCategories]);

  useEffect(() => {
    if (!value || !serviceCategories) return;

    const cats = value.serviceCategories;
    if (!cats || (cats ?? []).length === 0) return;

    setMacroCats(cats);
  }, [value, serviceCategories]);

  const toggleMacroCat = useCallback((macroCatId: string) => {
    setMacroCats((pv) => {
      if (pv.includes(macroCatId)) {
        return [...pv].filter((v) => v !== macroCatId);
      } else {
        return [...pv, macroCatId];
      }
    });
  }, []);

  const [isCatEdit, setIsCatEdit] = useState(false);
  const [catEditValue, setCatEditValue] = useState<ServiceCategory | null>(
    null
  );

  const [macroCatDialogOpen, setMacroCatDialogOpen] = useState(false);

  useEffect(() => {
    if (isCatEdit) return;
    setCatEditValue(null);
  }, [isCatEdit]);

  const [bevyTimeSlot, setBevyTimeSlot] = useState<Date | undefined | null>(
    (function (): Date | null {
      if (!value?.bevyTimeSlotInUtc) {
        return null;
      }

      let dt = DateTime.fromISO(
        `1970-01-01T${value?.bevyTimeSlotInUtc}:00.000Z`
      );
      if (DateTime.now().isInDST) {
        dt = dt.plus({ hour: 1 });
      }

      return dt.toJSDate();
    })()
  );

  const [providedBy, setProvidedBy] = useState<ServiceProvidedBy | undefined>(
    value?.providedBy
  );
  const [stripePrivateApiKey, setStripePrivateApiKey] = useState<
    string | undefined
  >(value?.stripePrivateApiKey);
  const [stripePublicApiKey, setStripePublicApiKey] = useState<
    string | undefined
  >(value?.stripePublicApiKey);
  const [weekSlots, setWeekSlots] = useState<ServiceTimeSlot[][] | undefined>(
    value?.weekSlots
  );
  const [active, setActive] = useState<boolean>(value?.active ?? true);
  const [alertEmail, setAlertEmail] = useState(value?.alertEmail);
  const [displayInApp, setDisplayInApp] = useState<boolean>(
    value?.displayInApp ?? true
  );
  const [dayReservationMode, setDayReservationMode] = useState<
    ServiceDaySlotAvailabilityOptionsModes | undefined
  >(value?.daySlotAvailabilityOptions?.mode);
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<
    string | undefined
  >(value?.privacyPolicyLink);
  const [termsAndConditionLink, setTermsAndConitionLink] = useState<
    string | undefined
  >(value?.termsAndConditionLink);
  const [tacUrl, setTacUrl] = useState<string | undefined>(value?.tacUrl);
  const [tacServiceId, setTacServiceId] = useState<string | undefined>(
    value?.tacServiceId
  );
  const [tacQuotationId, setTacQuotationId] = useState<string | undefined>(
    value?.tacQuotationId
  );
  const [sortIndex, setSortIndex] = useState(value?.sortIndex ?? 999);
  const [laundryOptions, setLaundryOptions] = useState<
    ServiceLaundryOptions | undefined
  >(value?.laundryOptions);
  const [composableOptions, setComposableOptions] = useState<
    ComposableServiceConfig | undefined
  >(value?.composableOptions);
  const [bookingInfo, setBookingInfo] = useState(
    value?.bookingInfo ?? {
      periods: [],
      durations: [],
      intervalBetweenStartsInMinutes: 0,
      defaultAdvanceInMinutes: 0,
    }
  );

  const surveyMapper = useCallback(
    (items: ServiceFormQuestion[] | undefined) => {
      let result: ServiceFormQuestionUIModel[];
      if (items && items.length > 0) {
        result = items.map((item, index) => {
          const v: ServiceFormQuestionUIModel = {
            ...item,
            deleted: false,
            sortIndex: (index + 1) * 10,
          };
          return v;
        });
      } else {
        result = [];
      }
      return result;
    },
    []
  );
  const [survey, setSurvey] = useState(surveyMapper(value?.reservationSurvey));
  const [hideSlotEndTime, setHideSlotEndTime] = useState(
    value?.hideSlotEndTime || false
  );

  const initialSlotDuration = useMemo(() => {
    if (value?.weekSlots) {
      for (const weekSlot of value.weekSlots) {
        for (const innerSlot of weekSlot) {
          if (innerSlot.durationInMinutes) {
            return innerSlot.durationInMinutes;
          }
        }
      }
    }

    return 120;
  }, [value?.weekSlots]);

  const [slotDuration, setSlotDuration] = useState<number>(initialSlotDuration);
  const slotDurationDisabled = useMemo(() => {
    return weekSlots?.some((v) => v.length > 0);
  }, [weekSlots]);

  const currentServiceOverridableProps: ServiceOverridableProps =
    useMemo(() => {
      return {
        phone,
        image,
        attachments,
        type,
        providedBy,
        stripePrivateApiKey,
        stripePublicApiKey,
        weekSlots,
        active,
        displayInApp,
        daySlotAvailabilityOptions:
          dayReservationMode !== undefined
            ? {
                mode: dayReservationMode,
              }
            : void 0,
      };
    }, [
      active,
      attachments,
      dayReservationMode,
      displayInApp,
      image,
      phone,
      providedBy,
      stripePrivateApiKey,
      stripePublicApiKey,
      type,
      weekSlots,
    ]);

  const askCompatiblePlaces = useMemo(() => {
    return (
      type === ServiceType.LONGEVITY ||
      Boolean(composableOptions?.askAmenityPlace)
    );
  }, [composableOptions?.askAmenityPlace, type]);

  const askSlotMaxUnits = useMemo(() => {
    return (
      dayReservationMode === ServiceDaySlotAvailabilityOptionsModes.NTH_PER_SLOT
    );
  }, [dayReservationMode]);

  const askReservationOptions = useMemo(() => {
    return (
      dayReservationMode ===
      ServiceDaySlotAvailabilityOptionsModes.ONE_PER_CATEGORY
    );
  }, [dayReservationMode]);

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    const reservationSurveyNotDeleted = survey.filter(
      (item) => item.deleted === false
    );
    const reservationSurvey = reservationSurveyNotDeleted.map((item) => {
      const o: ServiceFormQuestionGqlInputModel = { ...item };
      delete o["deleted"];
      return o;
    });

    let bevyTimeSlotInUtc: string | undefined = undefined;
    if (bevyTimeSlot) {
      console.debug(`setting bevyTimeSlotInUtc`);
      bevyTimeSlotInUtc = DateTime.fromJSDate(bevyTimeSlot).toFormat("HH:mm");
    }

    const servicesWithoutBookingInfo = [ServiceType.MAMACLEAN];
    const noBookingInfo = servicesWithoutBookingInfo.indexOf(type!) >= 0;
    const _bookingInfo: BookingInfoDto = { ...bookingInfo };
    if (!_bookingInfo.intervalBetweenStartsInMinutes)
      _bookingInfo.intervalBetweenStartsInMinutes = 0;
    if (!_bookingInfo.defaultAdvanceInMinutes)
      _bookingInfo.defaultAdvanceInMinutes = 0;
    if (!bookingInfo.durations || bookingInfo.durations.length === 0)
      _bookingInfo.durations = [0];

    invokeCreateOrUpdateService({
      variables: {
        id: (value as Service)?.id,
        serviceData: {
          buildings,
          phone,
          image,
          attachments,
          type,
          providedBy,
          stripePrivateApiKey,
          stripePublicApiKey,
          weekSlots,
          active,
          alertEmail,
          displayInApp,
          daySlotAvailabilityOptions:
            dayReservationMode !== undefined
              ? {
                  mode: dayReservationMode,
                }
              : void 0,
          i18n: i18nFields,
          privacyPolicyLink,
          termsAndConditionLink,
          laundryOptions:
            laundryOptions !== undefined ? laundryOptions : void 0,
          composableOptions,
          reservationSurvey,
          hideSlotEndTime,
          bevyTimeSlotInUtc,
          bookingInfo: noBookingInfo ? void 0 : _bookingInfo,
          serviceCategories: macroCats,
          tacUrl,
          tacServiceId,
          tacQuotationId,
          sortIndex,
        },
      },
    });
  };

  if (createOrUpdateServiceData && !updated) {
    onValueUpdatedSuccessfully(createOrUpdateServiceData.createSystemService);
    setUpdated(true);
  }

  const askSlots = useMemo(() => {
    return (
      type === ServiceType.CARWASH ||
      type === ServiceType.LONGEVITY ||
      composableOptions?.askAvailabilitySlots
    );
  }, [composableOptions?.askAvailabilitySlots, type]);

  const askCategories = useMemo(() => {
    return type === ServiceType.LONGEVITY;
  }, [type]);

  const askRequiredTime = useMemo(() => {
    return (
      type === ServiceType.LONGEVITY || composableOptions?.askAvailabilitySlots
    );
  }, [composableOptions?.askAvailabilitySlots, type]);

  const askProducts = useMemo(() => {
    return (
      type === ServiceType.LONGEVITY || Boolean(composableOptions?.askProducts)
    );
  }, [composableOptions?.askProducts, type]);

  const askDaySlotReservationOptions = useMemo(() => {
    return (
      type === ServiceType.LONGEVITY || composableOptions?.askAvailabilitySlots
    );
  }, [composableOptions?.askAvailabilitySlots, type]);

  const askReservationTimeOffsetInHours = useMemo(() => {
    return (
      type === ServiceType.LONGEVITY || composableOptions?.askAvailabilitySlots
    );
  }, [composableOptions?.askAvailabilitySlots, type]);

  const serviceListItemsReadOnly = useMemo(() => {
    return type === ServiceType.MAMACLEAN;
  }, [type]);

  const askLaundryOptions = useMemo(() => {
    return type === ServiceType.MAMACLEAN;
  }, [type]);

  const askServiceComposableOptions = useMemo(() => {
    return type === ServiceType.GENERIC_COMPOSABLE;
  }, [type]);

  const hideProducts = useMemo(() => {
    return type
      ? [ServiceType.BEVY, ServiceType.MAMACLEAN].includes(type)
      : false;
  }, [type]);

  const isBevy = useMemo(() => {
    return type === ServiceType.BEVY;
  }, [type]);

  const suggestedCategories = useMemo(() => {
    switch (type) {
      case ServiceType.CARWASH:
        return {
          it: ["SUV", "Auto"],
          en: ["SUV", "Car"],
        };
      case ServiceType.LONGEVITY:
        return {
          it: ["Estetica", "Salute", "Viso e corpo"],
          en: ["Aesthetics", "Health", "Face and body"],
        };
      default:
        return {
          it: [],
          en: [],
        };
    }
  }, [type]);

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateServiceData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                required
                i18nFieldName="name"
                className={classes.textField}
                label="Nome"
                variant="outlined"
                value={i18nFields}
                onChange={(e) => setI18nFields(e)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <PhoneInput
                required
                className={classes.textField}
                label="Telefono"
                variant="outlined"
                defaultValue={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Grid>

            {isAdminUser && (
              <>
                <Grid item xs={2}>
                  <TextField
                    type="number"
                    variant="outlined"
                    label="Ordine"
                    className={classes.textField}
                    value={sortIndex}
                    onChange={(e) =>
                      setSortIndex(Number.parseInt(e.target.value))
                    }
                  ></TextField>
                </Grid>
                <Grid item sm={12} xs={12}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 24 }}
                  >
                    <p>Macrocategorie Navigazione:</p>

                    <Button onClick={() => setIsCatEdit((pv) => !pv)}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        {isCatEdit ? (
                          <>
                            <EditOff />
                            <div>Annulla Modifica Categorie</div>
                          </>
                        ) : (
                          <>
                            <Edit />
                            <div>Attiva Modifica Categorie</div>
                          </>
                        )}
                      </div>
                    </Button>
                  </div>

                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                    {sortedCategories?.map((serviceCat, index) => {
                      const text = `
                      ${serviceCat.i18n.name_it} 
                      (${serviceCat.sortIndex})`;
                      return isCatEdit ? (
                        <Button
                          onClick={() => {
                            setCatEditValue(serviceCat);
                            setMacroCatDialogOpen(true);
                          }}
                          style={{
                            padding: "6px 4px",
                            textTransform: "none",
                            fontSize: 14,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <Edit /> {text}
                          </div>
                        </Button>
                      ) : (
                        <label
                          key={index}
                          htmlFor={serviceCat.id}
                          style={{ cursor: "pointer" }}
                        >
                          <Checkbox
                            id={serviceCat.id}
                            checked={macroCats.includes(serviceCat.id)}
                            onChange={() => toggleMacroCat(serviceCat.id)}
                          />
                          {text}
                        </label>
                      );
                    })}

                    {!isCatEdit && (
                      <Button
                        style={{ padding: "0 10px" }}
                        onClick={() => setMacroCatDialogOpen(true)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <Add />
                          <p style={{ margin: 0 }}>Aggiungi Categoria</p>
                        </div>
                      </Button>
                    )}
                  </div>
                </Grid>
              </>
            )}

            <DialogWithActions
              open={macroCatDialogOpen}
              fullScreen={false}
              setOpen={() => setMacroCatDialogOpen((pv) => !pv)}
              title={`${
                catEditValue ? "Modifica" : "Crea"
              } Macrocategoria Navigazione`}
            >
              <ServiceMacroCatForm
                catEditValue={catEditValue}
                onToggle={() => setMacroCatDialogOpen(false)}
                onServiceCategoryCreate={onServiceCategoryCreate}
                onServiceCategoryUpdate={(v) => {
                  onServiceCategoryUpdate(v);
                  setIsCatEdit(false);
                }}
              />
            </DialogWithActions>

            {!isServiceProviderUser && (
              <>
                <Grid item sm={12} xs={12}>
                  <p>Tipologia Servizio:</p>
                  <Select
                    variant="outlined"
                    displayEmpty
                    className={classes.textField}
                    value={type}
                    onChange={(evt) => {
                      if (evt !== null && evt !== void 0) {
                        setType(Number(evt.target.value));
                      } else {
                        setType(void 0);
                      }
                    }}
                  >
                    <MenuItem value={undefined} disabled>
                      Seleziona tipologia servizio
                    </MenuItem>
                    {Object.entries(serviceTypeOpts).map(([key, value]) => (
                      <MenuItem key={key} value={Number(key)}>
                        <ListItemText primary={value} />
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {isBevy && (
                  <Grid item sm={12} xs={12}>
                    <div>
                      <TimePicker
                        clearable
                        required
                        className={classes.textField}
                        inputVariant="outlined"
                        ampm={false}
                        label="Slot orario Bevy"
                        value={bevyTimeSlot}
                        onChange={setBevyTimeSlot}
                      />
                    </div>
                  </Grid>
                )}
              </>
            )}
            {!isServiceProviderUser && (
              <>
                <Grid item sm={4} xs={4}>
                  Attivo?
                  <Checkbox
                    checked={active}
                    onChange={(_evt, checked) => setActive(!active)}
                  />
                </Grid>
                <Grid item sm={4} xs={4}>
                  Mostra in app?
                  <Checkbox
                    checked={displayInApp}
                    onChange={(_evt, checked) => setDisplayInApp(!displayInApp)}
                  />
                </Grid>
              </>
            )}
            {!isServiceProviderUser && (
              <Grid item sm={12} xs={12}>
                <I18NTextField
                  required
                  multiline={true}
                  i18nFieldName="description"
                  className={classes.textField}
                  label="Descrizione"
                  variant="outlined"
                  value={i18nFields}
                  onChange={(e) => setI18nFields(e)}
                />
              </Grid>
            )}
            {!isServiceProviderUser && (
              <Grid item sm={4} xs={12}>
                <ImagePicker
                  width={"100%"}
                  height={"auto"}
                  defaultValue={image?.url}
                  onUploaded={(entry) =>
                    setImage({
                      url: entry.url,
                    })
                  }
                />
              </Grid>
            )}
            {!isServiceProviderUser && (
              <Grid item sm={4} xs={12}>
                <AttachmentsPicker
                  defaultValue={attachments}
                  onUpdated={(entries) => setAttachments(entries)}
                />
              </Grid>
            )}
            {!isServiceProviderUser && (
              <Grid item sm={4} xs={12}>
                <GqlRecordPicker
                  className={classes.textField}
                  multiple={false}
                  value={providedBy}
                  getOptionLabel={(record) => record.username ?? record.name}
                  placeHolder={"Fornitore"}
                  renderOption={(record) => record.username ?? record.name}
                  onValueChange={(value) =>
                    setProvidedBy(
                      value
                        ? {
                            id: value.id,
                            name: value.username,
                          }
                        : void 0
                    )
                  }
                  query={QUERIES.USERS}
                  queryVariables={{
                    role: "service_provider",
                  }}
                  queryResultDataField={"users"}
                />
              </Grid>
            )}
            <Grid item sm={4}>
              <TextField
                variant={"outlined"}
                style={{ width: "100%" }}
                className={classes.textField}
                label="Email di notifica"
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                i18nFieldName="infoBoxTitle"
                className={classes.textField}
                label="Titolo info-box in app"
                multiline={true}
                variant="outlined"
                value={i18nFields}
                onChange={(e) => setI18nFields(e)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                i18nFieldName="infoBoxContent"
                className={classes.textField}
                label="Descrizione info box in app"
                multiline={true}
                rows={5}
                variant="outlined"
                value={i18nFields}
                onChange={(e) => setI18nFields(e)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                i18nFieldName="reservationDeleteConditions"
                className={classes.textField}
                label="Condizioni di cancellazione"
                multiline={true}
                rows={5}
                variant="outlined"
                value={i18nFields}
                onChange={(e) => setI18nFields(e)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                i18nFieldName="reservationUsefulInfoBoxContent"
                className={classes.textField}
                label="Contenuto box informazioni utili"
                multiline={true}
                rows={5}
                variant="outlined"
                value={i18nFields}
                onChange={(e) => setI18nFields(e)}
              />
            </Grid>
            {askServiceComposableOptions && (
              <Grid item sm={4} xs={12}>
                <I18NTextField
                  i18nFieldName="reservationOkPushMessage"
                  className={classes.textField}
                  label="Messaggio notifica PUSH esito positivo"
                  multiline={true}
                  rows={5}
                  variant="outlined"
                  value={i18nFields}
                  onChange={(e) => setI18nFields(e)}
                />
              </Grid>
            )}
            {askServiceComposableOptions && (
              <Grid item sm={4} xs={12}>
                <I18NTextField
                  i18nFieldName="reservationKoPushMessage"
                  className={classes.textField}
                  label="Messaggio notifica PUSH esito negativo"
                  multiline={true}
                  rows={5}
                  variant="outlined"
                  value={i18nFields}
                  onChange={(e) => setI18nFields(e)}
                />
              </Grid>
            )}
            <Grid item sm={4} xs={12}>
              <TextField
                className={classes.textField}
                label="Api KEY Privata Stripe"
                placeholder="sk_****"
                variant="outlined"
                value={stripePrivateApiKey}
                onChange={(e) => setStripePrivateApiKey(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                className={classes.textField}
                label="Api KEY Pubblica Stripe"
                placeholder="pk_****"
                variant="outlined"
                value={stripePublicApiKey}
                onChange={(e) => setStripePublicApiKey(e.target.value)}
              />
            </Grid>
            {askServiceComposableOptions && (
              <Grid item xs={12}>
                <Paper style={{ padding: "1rem" }}>
                  <ServiceCompositionOptsEditor
                    value={composableOptions ?? ({} as ComposableServiceConfig)}
                    onChange={setComposableOptions}
                  />
                </Paper>
              </Grid>
            )}
            {askServiceComposableOptions && (
              <Grid item xs={12}>
                <Paper style={{ padding: "1rem" }}>
                  <ServiceSurvey
                    items={survey}
                    onUpdate={setSurvey}
                  ></ServiceSurvey>
                </Paper>
              </Grid>
            )}
            <Grid item sm={4} xs={12}>
              <TextField
                className={classes.textField}
                label="Link alla privacy policy"
                placeholder="https://.../"
                variant="outlined"
                value={privacyPolicyLink}
                onChange={(e) => setPrivacyPolicyLink(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                className={classes.textField}
                label="Link ai termini e condizioni del servizio"
                placeholder="https://.../"
                variant="outlined"
                value={termsAndConditionLink}
                onChange={(e) => setTermsAndConitionLink(e.target.value)}
              />
            </Grid>
            {type === ServiceType.TAC && (
              <Grid item sm={6} xs={12}>
                <TextField
                  className={classes.textField}
                  label="Base URL per la webview Tac!"
                  placeholder="https://.../"
                  variant="outlined"
                  value={tacUrl}
                  onChange={(e) => setTacUrl(e.target.value.trim())}
                />
              </Grid>
            )}
            {type === ServiceType.TAC && (
              <Grid item sm={6} xs={12}>
                <TextField
                  className={classes.textField}
                  label="ID del servizio Tac!"
                  placeholder="TAC_XX"
                  variant="outlined"
                  value={tacServiceId}
                  onChange={(e) => setTacServiceId(e.target.value.trim())}
                />
              </Grid>
            )}
            {askLaundryOptions && (
              <ServiceLaundryOptionsEditor
                value={laundryOptions}
                onChange={setLaundryOptions}
              />
            )}
            {!isServiceProviderUser && (
              <Grid item xs={12}>
                <ServiceBuildingsPicker
                  buildings={buildings}
                  onChange={(updatedBuildings) =>
                    setBuildings(updatedBuildings)
                  }
                  askCompatiblePlaces={askCompatiblePlaces}
                  defaultOverridableProps={currentServiceOverridableProps}
                />
              </Grid>
            )}
            {askDaySlotReservationOptions && (
              <Grid item sm={12} xs={12}>
                <Typography variant="caption">
                  Modalità di calcolo per le prenotazioni
                </Typography>
                <Select
                  variant="outlined"
                  displayEmpty
                  className={classes.textField}
                  value={dayReservationMode}
                  onChange={(evt) => {
                    if (evt !== null && evt !== void 0) {
                      setDayReservationMode(Number(evt.target.value));
                    } else {
                      setDayReservationMode(void 0);
                    }
                  }}
                >
                  <MenuItem value={undefined} disabled>
                    Modalità di calcolo disponibilità slot
                  </MenuItem>
                  {Object.entries(serviceDaySlotAvailabilityOptions).map(
                    ([key, value]) => (
                      <MenuItem
                        selected={Number(key) === dayReservationMode}
                        key={key}
                        value={Number(key)}
                      >
                        <ListItemText primary={value} />
                      </MenuItem>
                    )
                  )}
                </Select>
              </Grid>
            )}
            {askSlots && !isBevy && (
              <Grid item sm={12} xs={12}>
                <Grid container>
                  <Grid item xs={12} md={3}>
                    <TextField
                      className={classes.textField}
                      disabled={slotDurationDisabled}
                      label="Durata slot in minuti"
                      variant="outlined"
                      value={slotDuration}
                      onChange={(e) => setSlotDuration(Number(e.target.value))}
                    />
                    {slotDurationDisabled && (
                      <span>
                        Per modificare la durata dello slot, devi eliminare
                        tutti gli slot presenti nel pannello sottostante.
                      </span>
                    )}
                  </Grid>
                  <Grid item md={6}>
                    <Checkbox
                      edge="start"
                      checked={hideSlotEndTime}
                      onChange={(e, value) => setHideSlotEndTime(value)}
                      disableRipple
                    />{" "}
                    Mostra solo orario di partenza degli slot
                  </Grid>
                </Grid>
                {false && (
                  <ServiceTabPanel
                    askMaxUnits={askSlotMaxUnits}
                    askReservationOptions={askReservationOptions}
                    askReservationTimeOffsetInHours={
                      askReservationTimeOffsetInHours
                    }
                    reservationOptionsCategories={suggestedCategories.it}
                    slotDurationInMinutes={slotDuration}
                    weekSlots={weekSlots ?? [[], [], [], [], [], [], []]}
                    weekSlotsChange={setWeekSlots}
                  />
                )}
                <BookingInfo
                  value={bookingInfo}
                  onChange={setBookingInfo}
                  type={BookingInfoServiceTypeEnum.SERVICE}
                />
              </Grid>
            )}
            {value?.id && !hideProducts && (
              <Grid item sm={12} xs={12}>
                <ServiceListItemsEditorTabs
                  service={value}
                  isComposable={askServiceComposableOptions}
                  suggestedCategories={suggestedCategories}
                  askCategories={askCategories}
                  askRequiredTime={askRequiredTime}
                  askProducts={askProducts}
                  readonly={serviceListItemsReadOnly}
                />
              </Grid>
            )}
            <Grid item sm={12} xs={12}>
              <Button
                className={classes.textField}
                type="submit"
                variant="contained"
                color="primary"
              >
                {!loading ? "Salva" : <CircularProgress />}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}
